import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { X, Download } from "lucide-react";
import { toast } from "sonner";
import { LifeInNumberOnes } from "@/components/LifeInNumberOnes";
import { SportsBirthYear } from "@/components/Sports";
import { MoviesBirthYear, MoviesBirthdayTwins } from "@/components/Movies";
import { downloadTwinShareCard, drawTwinShareCard, CARD_THEMES } from "@/lib/twinShareCard";
import { track } from "@/lib/analytics";
import api, { formatApiErrorDetail } from "@/lib/api";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const field = "w-full h-11 rounded-full border border-[#e5e7eb] bg-white px-4 text-[14px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-gold";
const thisYear = new Date().getFullYear();

const personName = (t) => (t || "").replace(/^Birth of /i, "").replace(/ born$/i, "");

const shareText = (twins) => {
  const names = twins.slice(0, 3).map((t) => personName(t.event_title));
  const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "some remarkable people";
  return `I share my birthday with ${list}! Discover yours on OddlyTrue`;
};

const ShareButtons = ({ r }) => {
  const twins = r.twins;
  const [busy, setBusy] = useState(false);
  const [theme, setTheme] = useState("ink");
  const previewRef = useRef(null);
  const cardData = { name: r.name, dateLabel: r.date_label, signName: r.sign_name, glyph: r.glyph, twins: twins.slice(0, 3).map((t) => ({ name: personName(t.event_title), year: t.year })) };
  const text = shareText(twins);

  useEffect(() => {
    if (previewRef.current) drawTwinShareCard(previewRef.current, { ...cardData, theme });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  const downloadCard = async () => {
    setBusy(true);
    try {
      await downloadTwinShareCard({ ...cardData, theme });
      track("share_card_download", { date: r.dob ? r.dob.slice(8, 10) + "-" + r.dob.slice(5, 7) : "", category: "share", theme, sign: r.sign });
      toast.success("Share card downloaded — post it to your story");
    } catch { toast.error("Could not build the share card"); }
    finally { setBusy(false); }
  };
  const site = window.location.origin;
  const wa = `https://wa.me/?text=${encodeURIComponent(`${text} ${site}`)}`;
  const x = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(site)}`;
  const btn = "flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full border border-[#e5e7eb] bg-white text-ink text-[13px] font-semibold hover:border-gold hover:text-gold transition-colors";
  return (
    <div className="mt-4" data-testid="share-twins">
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold">Share my twins</p>
      <div className="mt-2 flex gap-2">
        <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className={btn}>
          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.4c1.4.8 3.1 1.2 4.8 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3.1.8.8-3-.2-.3C4.1 15 3.7 13.5 3.7 12c0-4.6 3.7-8.3 8.3-8.3s8.3 3.7 8.3 8.3-3.7 8.2-8.3 8.2z"/></svg>
          Share to WhatsApp
        </a>
        <a href={x} target="_blank" rel="noopener noreferrer" data-testid="share-x" className={btn}>
          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M18.9 2H22l-7.5 8.6L23 22h-6.8l-5.3-7-6.1 7H1.6l8-9.2L1 2h7l4.8 6.4L18.9 2zm-1.2 18h1.9L7.4 3.9H5.4L17.7 20z"/></svg>
          Share to X
        </a>
      </div>
      <div className="mt-3 flex gap-3 items-stretch bg-white border border-[#e5e7eb] rounded-2xl p-3" data-testid="share-card-builder">
        <canvas ref={previewRef} data-testid="share-card-preview" className="w-[90px] h-[160px] rounded-lg border border-[#e5e7eb] shrink-0 bg-paper" aria-label="Share card preview" />
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <p className="text-[12px] font-semibold text-ink">Story card · 1080×1920</p>
            <div className="mt-2 flex gap-2" role="radiogroup" aria-label="Card style">
              {Object.values(CARD_THEMES).map((t) => (
                <button key={t.id} type="button" role="radio" aria-checked={theme === t.id} data-testid={`card-style-${t.id}`} onClick={() => setTheme(t.id)}
                  className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[12px] font-semibold transition-colors ${theme === t.id ? "border-gold bg-paper text-ink" : "border-[#e5e7eb] bg-white text-ink-muted hover:border-gold"}`}>
                  <span className="w-3 h-3 rounded-full border border-[#e5e7eb]" style={{ background: t.bg }} />{t.label}
                </button>
              ))}
            </div>
          </div>
          <button type="button" onClick={downloadCard} disabled={busy} data-testid="share-card-download" className="mt-2 w-full inline-flex items-center justify-center gap-2 h-10 rounded-full bg-ink text-white text-[13px] font-semibold hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
            <Download className="w-4 h-4" /> {busy ? "Building your card..." : "Download PNG"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Results = ({ r, onClose, doneLabel = "Done" }) => (
  <div data-testid="birthday-results">
    <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold">Your birthday almanac</p>
    <h2 className="mt-1 text-2xl font-bold text-ink">Happy almost-birthday, {r.name}!</h2>
    <div className="mt-4 flex items-center gap-3 bg-paper border border-[#e5e7eb] rounded-2xl p-4">
      <span className="w-14 h-14 rounded-xl bg-white border border-[#e5e7eb] flex items-center justify-center text-[26px] text-ink">{r.glyph}{"\uFE0E"}</span>
      <div>
        <p className="text-[20px] font-bold text-ink">{r.sign_name}</p>
        <p className="text-[12px] text-ink-muted">{r.dates} • born {r.date_label}</p>
      </div>
    </div>
    <p className="mt-3 text-[13px] text-ink-muted leading-relaxed">{r.traits}</p>
    <p className="mt-3 bg-white border-l-4 border-gold rounded-r-xl px-4 py-3 text-[14px] leading-[1.7] text-[#333]">{r.horoscope}</p>
    <h3 className="mt-5 text-[15px] font-bold text-ink">You share your {r.date_label} birthday with...</h3>
    {r.twins.length === 0 ? (
      <p className="mt-2 text-[13px] text-ink-muted">No famous births on record for this date yet — you might be the first.</p>
    ) : (
      <ul className="mt-2 space-y-2 max-h-56 overflow-auto pr-1">
        {r.twins.map((t) => (
          <li key={t.id} className="flex items-center gap-3 bg-white border border-[#e5e7eb] rounded-xl px-3 py-2">
            <img src="/images/categories/birth.png" alt="" className="w-8 h-8 rounded-lg bg-paper" />
            <span className="text-[14px] font-semibold text-ink truncate">{personName(t.event_title)}</span>
            <span className="ml-auto text-[13px] font-bold text-gold">{t.year}</span>
          </li>
        ))}
      </ul>
    )}
    <LifeInNumberOnes items={r.number_ones} compact className="mt-4" />
    <SportsBirthYear items={r.sports_birth_year} year={r.dob ? Number(String(r.dob).slice(0, 4)) : null} compact className="mt-4" />
    <MoviesBirthYear data={r.movies_birth_year} compact className="mt-4" />
    <MoviesBirthdayTwins data={r.movies_birthday} compact className="mt-4" />
    <ShareButtons r={r} />
    <p className="mt-4 text-[11px] text-ink-muted">We've saved your birthday so your horoscope and birthday insights can reach {r.email || "your inbox"}.</p>
    <button type="button" onClick={onClose} data-testid="birthday-done" className="mt-4 w-full h-11 rounded-full bg-ink text-white font-semibold text-[14px] hover:bg-gold hover:text-ink transition-colors">{doneLabel}</button>
  </div>
);


/** The capture form + results, usable inline (Birthdays page box) or inside the modal. */
export const BirthdayCaptureForm = ({ source = "birthday_capture", onDone, doneLabel = "Done" }) => {
  const [form, setForm] = useState({ name: "", day: "", month: "", year: "", email: "", consent: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.day || !form.month || !form.year) return setError("Please enter your full date of birth.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return setError("Please enter a valid email address.");
    if (!form.consent) return setError("Please tick the consent box so we can send your horoscope.");
    setBusy(true);
    try {
      const dob = `${form.year}-${String(form.month).padStart(2, "0")}-${String(form.day).padStart(2, "0")}`;
      const r = await api.post("/birthday-capture", { name: form.name.trim(), dob, email: form.email.trim(), consent: true, source });
      track("birthday_twins_submit", { date: `${String(form.day).padStart(2, "0")}-${String(form.month).padStart(2, "0")}`, category: "lead", source, sign: r.data.sign });
      setResult({ ...r.data, email: form.email.trim() });
      try { localStorage.setItem("oddlytrue_birthday", `${String(form.day).padStart(2, "0")}-${String(form.month).padStart(2, "0")}`); } catch (err) { /* ignore */ }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (result) return <Results r={result} onClose={() => { setResult(null); onDone?.(); }} doneLabel={doneLabel} />;
  return (
          <form onSubmit={submit} className="space-y-4" data-testid="birthday-form">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold">Birthday twins</p>
              <h2 className="mt-1 text-2xl font-bold text-ink">See who shares YOUR birthday</h2>
              <p className="mt-1 text-[13px] text-ink-muted">Your star sign, today's reading and the famous people born on your day.</p>
            </div>
            <input data-testid="birthday-name" required value={form.name} onChange={set("name")} placeholder="Your first name" className={field} />
            <div className="grid grid-cols-3 gap-2">
              <select data-testid="birthday-day" value={form.day} onChange={set("day")} className={field} required>
                <option value="">Day</option>{Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select data-testid="birthday-month" value={form.month} onChange={set("month")} className={field} required>
                <option value="">Month</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select data-testid="birthday-year" value={form.year} onChange={set("year")} className={field} required>
                <option value="">Year</option>{Array.from({ length: 110 }, (_, i) => thisYear - i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <input data-testid="birthday-email" type="email" required value={form.email} onChange={set("email")} placeholder="Email address" className={field} />
            <label className="flex items-start gap-3 text-[13px] text-ink cursor-pointer">
              <input data-testid="birthday-consent" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 w-4 h-4 accent-gold" />
              <span>Yes, send me my daily horoscope and birthday insights</span>
            </label>
            {error && <p data-testid="birthday-error" className="text-[13px] text-red-600">{error}</p>}
            <button type="submit" disabled={busy} data-testid="birthday-submit" className="w-full h-12 rounded-full bg-gold text-ink font-semibold text-[15px] hover:bg-[#c19614] transition-colors disabled:opacity-60">
              {busy ? "Looking up your birthday..." : "Show my birthday twins →"}
            </button>
            <p className="text-[11px] text-[#9ca3af]">We never sell your email. Unsubscribe any time.</p>
          </form>
  );
};

export const BirthdayCaptureModal = ({ open, onClose, source = "birthday_capture" }) => {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div data-testid="birthday-modal" className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-ink/50 p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-lg bg-paper-light rounded-t-2xl sm:rounded-2xl p-6 shadow-xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end -mt-2 -mr-2">
          <button type="button" onClick={onClose} aria-label="Close" data-testid="birthday-close" className="w-9 h-9 rounded-full hover:bg-paper flex items-center justify-center text-ink"><X className="w-5 h-5" /></button>
        </div>
        <BirthdayCaptureForm source={source} onDone={onClose} />
      </div>
    </div>
  );
};

/** Every "see who shares your birthday" link now lands on the Birthdays page, where the capture form lives in a box. */
export const BirthdayLink = ({ className = "", testid = "birthday-link", label = "See who shares YOUR birthday →" }) => (
  <Link to="/birthdays#twins" data-testid={testid} className={`text-[12px] font-semibold text-gold hover:underline ${className}`}>
    {label}
  </Link>
);
